// 「⚙ 设置」: a two-pane settings window (artin 11-02: 「左右分栏的那种，就是企业级的设置」). On the left a list of
// groups (账号, 我的状态, 语音与声音, ...) with a search box on top; on the right the chosen group's sections. On a phone
// (<= 640 px) it's a sheet from the bottom: the list of groups first, a tap opens one, 「‹」 goes back to the list.
// Anything can add a section:
//   import { addSettingsSection } from '/settings.js';
//   addSettingsSection({ id: 'noise', title: '降噪', icon: 'headphones', group: 'voice', order: 10, keywords: '噪音 风扇',
//     render(box) { box.append(...) } });
//   icon: a pixel icon name, optional. title: a function giving the text (title: () => t('voice.noise.title'), so it
//   follows 设置 → 语言), or still a string. group: one of GROUPS' ids below (default: HOME[id], else 「其他」). order:
//   where it sits in its group (smaller first). keywords: more words 搜索 finds it by (a string, or a function giving one).
// render() gets an empty element each time its group is shown (and when the language changes); build it with text
// nodes, not HTML. The window's own words are settings.* keys (docs/i18n.md).
// A centered window over a dimmed backdrop: Esc or a click outside closes it. On 账号, a header anyone may set
// (public/profile.js: who I am, my Raft server, 切换 Server and 退出登录):
//   setSettingsHeader((box) => { box.append(...) })
// openSettings(keyboard, where) opens the window from anywhere; where: a group or section id ('account', 'visits').
// Deep links: #settings opens it, #settings/<group or section id> opens it there (#settings/voice); while it's open
// the address bar says where it is, so the link can be copied.
// title: settings.group.<id> (搜索 also finds 账号 by settings.find.account: it has no sections of its own)
const GROUPS = [
  { id: 'account', icon: 'otter' }, { id: 'status', icon: 'dnd' }, { id: 'voice', icon: 'mic' }, { id: 'stt', icon: 'transcribing' },
  { id: 'look', icon: 'palette' }, { id: 'phone', icon: 'gamepad' }, { id: 'invite', icon: 'people' }, { id: 'tours', icon: 'bulb' },
  { id: 'admin', icon: 'wrench' }, { id: 'other', icon: 'gear' }, { id: 'about', icon: 'note' },
];
const gTitle = (g) => t(`settings.group.${g.id}`);
// where the sections that don't say their group go (their ids are the modules' own)
const HOME = {
  status: 'status', voice: 'voice', speaker: 'voice', sound: 'voice', stt: 'stt',
  pip: 'look', install: 'look', language: 'look', fullscreen: 'phone', stick: 'phone',
  guests: 'invite', visits: 'invite', calendar: 'account', tours: 'tours', floors: 'admin', 'map-editor': 'admin', 'agent-door': 'admin', devbots: 'admin', about: 'about',
};
// what else 搜索 finds a section by (besides its title, its group's and what it said the last time it was shown):
// settings.find.<section id>, in the reader's language
const FIND = new Set(['account', 'status', 'voice', 'speaker', 'sound', 'stt', 'pip', 'install', 'language', 'fullscreen', 'stick', 'guests', 'visits', 'calendar', 'tours', 'floors', 'map-editor', 'agent-door', 'devbots', 'about']);
const findWords = (id) => (FIND.has(id) ? t(`settings.find.${id.replace(/-(\w)/g, (_, c) => c.toUpperCase())}`) : '');
const sections = [];
let panel = null, nav = null, pane = null, top = null, header = null, find = null, title = null, backBtn = null;
let cur = null, view = 'list', want = null; // the group shown; on a phone the list or a group; a deep link not there yet
const seen = new Map(); // section id -> its text the last time it was shown (搜索 finds words in it)

export function addSettingsSection(sec) {
  const i = sections.findIndex((s) => s.id === sec.id);
  if (i >= 0) sections.splice(i, 1);
  sections.push({ order: 50, ...sec });
  sections.sort((a, b) => a.order - b.order);
  if (!shown()) return;
  if (settle()) return;
  drawNav();
  if (groupOf(sec) === cur) drawPane();
}
/** The window is open but shows nothing yet (opened by a deep link before the sections came), or the deep link's
 *  section just came: show it (or the first group; on a phone the list). True if it drew. */
function settle() {
  if (want && target(want)) { go(want); return true; }
  if (cur && target(cur)) return false;
  if (phone()) { cur = groups()[0]?.id ?? null; toList(); } else go(groups()[0]?.id);
  return true;
}
/** The header on 账号 (render gets an empty element each time 账号 is shown, or on refreshSettingsHeader). */
export function setSettingsHeader(render) { header = render; if (shown() && !settle()) { drawNav(); if (cur === 'account') drawPane(); } }
export function refreshSettingsHeader() { if (shown() && cur === 'account' && top?.isConnected) drawHeader(); }
/** Open the window (keyboard: focus goes into it, and back to where it was when it closes); where: a group or section id. */
export function openSettings(keyboard = false, where) { open(true, keyboard, where); }

const CSS_URL = new URL('./settings.css', import.meta.url).pathname; // its URL: Vite names the built file by its content (vite.config.mjs)
if (!document.querySelector(`link[href="${CSS_URL}"]`)) {
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = CSS_URL; document.head.appendChild(l);
}
import { icon, label, put } from '/icons.js';
import { t, tParts, onLang, LANGS, langPref, setLang } from '/i18n.js';
import { dropdown } from '/dropdown.js';
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const shown = () => !!panel && back.style.display !== 'none';
const phone = () => matchMedia('(max-width:640px)').matches;
const text = (s) => (typeof s.title === 'function' ? s.title() : s.title) ?? '';
const groupOf = (s) => (s && GROUPS.find((g) => g.id === (s.group ?? HOME[s.id])) ? s.group ?? HOME[s.id] : 'other');
const inGroup = (g) => sections.filter((s) => groupOf(s) === g);
const groups = () => GROUPS.filter((g) => (g.id === 'account' && header) || inGroup(g.id).length);
/** where: a group or a section id -> {g, s} (s: the section to scroll to), or null if there's none such (yet). */
function target(where) {
  if (!where) return null;
  if (groups().some((g) => g.id === where)) return { g: where };
  const s = sections.find((x) => x.id === where);
  return s ? { g: groupOf(s), s: s.id } : null;
}

function drawHeader() {
  top.replaceChildren();
  if (header) try { header(top); } catch (e) { console.error(e); }
}
function navItem(g, cls, sub) {
  const b = el('button', cls); b.type = 'button'; b.dataset.g = g.id;
  b.append(icon(g.icon), el('span', 'setnav-t', g.title ?? gTitle(g)));
  if (sub) b.append(el('span', 'setnav-sub', sub));
  return b;
}
function drawNav() {
  const q = find.value.trim().toLowerCase();
  const list = el('div', 'setnav-list'); list.setAttribute('role', q ? 'listbox' : 'tablist');
  if (!q) {
    for (const g of groups()) {
      const b = navItem(g, 'setnav-i');
      b.setAttribute('role', 'tab');
      if (g.id === cur && !(phone() && view === 'list')) { b.classList.add('on'); b.setAttribute('aria-selected', 'true'); } else b.setAttribute('aria-selected', 'false');
      b.onclick = () => go(g.id);
      list.append(b);
    }
  } else {
    const hits = [];
    for (const g of groups()) {
      const gt = gTitle(g);
      if (g.id === 'account' && header && (gt + ' ' + findWords('account')).toLowerCase().includes(q)) hits.push({ g, where: 'account', name: gt });
      for (const s of inGroup(g.id)) {
        const kw = typeof s.keywords === 'function' ? s.keywords() : [].concat(s.keywords ?? []).join(' ');
        const k = [text(s), gt, findWords(s.id), kw, seen.get(s.id) ?? ''].join(' ').toLowerCase();
        if (k.includes(q)) hits.push({ g, where: s.id, name: text(s), gt });
      }
    }
    for (const h of hits) {
      const b = navItem({ ...h.g, title: h.name }, 'setnav-i hit', !h.gt || h.name === h.gt ? '' : h.gt);
      b.setAttribute('role', 'option'); b.dataset.where = h.where;
      b.onclick = () => go(h.where);
      list.append(b);
    }
    if (!hits.length) list.append(el('p', 'setnav-none', t('settings.find.none', { q: find.value.trim() })));
  }
  const old = nav.querySelector('.setnav-list');
  if (old) old.replaceWith(list); else nav.append(list);
}
function drawPane() {
  pane.replaceChildren();
  const g = GROUPS.find((x) => x.id === cur);
  if (!g) return;
  pane.append(put(el('h2', 'setpane-h'), g.icon, gTitle(g)));
  if (cur === 'account' && header) { top = el('div', 'settop'); pane.append(top); drawHeader(); }
  const list = inGroup(cur);
  for (const s of list) {
    const box = el('section', 'setsec'); box.dataset.id = s.id;
    // one section alone in its group: the group's title says it already
    if (list.length > 1 || cur === 'account') box.append(put(el('h3'), s.icon, text(s)));
    const inner = el('div', 'setbody'); box.append(inner);
    try { s.render(inner); } catch (e) { console.error(e); inner.textContent = t('settings.broken'); }
    pane.append(box);
    seen.set(s.id, inner.textContent.slice(0, 600));
  }
}
/** The window's own state: which view (a phone shows the list or a group), the header's title, the address bar. */
function frame() {
  const p = phone(), inPane = !p || view === 'pane';
  panel.classList.toggle('in-pane', p && view === 'pane');
  backBtn.style.display = p && view === 'pane' ? '' : 'none';
  const g = GROUPS.find((x) => x.id === cur);
  put(title, p && view === 'pane' ? g?.icon : 'gear', p && view === 'pane' && g ? gTitle(g) : t('settings.title'));
  hash(shown() ? (inPane && cur ? `#settings/${cur}` : '#settings') : '');
}
// the address bar: only when it says nothing else (a guest or 串门 link's hash is read once at start, public/guest.js)
function hash(h) {
  const now = location.hash;
  if (now && !/^#settings(\/|$)/.test(now)) return;
  if (now === h || (!now && !h)) return;
  try { history.replaceState(history.state, '', location.pathname + location.search + h); } catch {}
}
/** Show a group (or a section in its group, scrolled to and lit up for a moment). */
function go(where, keyboard = false) {
  const t = target(where) ?? target(groups()[0]?.id);
  want = target(where) ? null : want;
  if (!t) { drawNav(); frame(); return; }
  const refocus = phone() && (panel.contains(document.activeElement) || document.activeElement === document.body); // the tapped row goes away with the list
  cur = t.g; view = 'pane';
  drawNav(); drawPane(); frame();
  pane.scrollTop = 0;
  if (t.s) {
    const box = pane.querySelector(`section[data-id="${CSS.escape(t.s)}"]`);
    if (box) { box.scrollIntoView({ block: 'start' }); box.classList.add('flash'); setTimeout(() => box.classList.remove('flash'), 1400); }
  }
  if (keyboard) (pane.querySelector('button,a[href],input,select,textarea') ?? nav.querySelector('.setnav-i.on'))?.focus();
  else if (refocus && !document.activeElement?.offsetParent) backBtn.focus({ preventScroll: true });
}
function toList() {
  const refocus = panel.contains(document.activeElement) || document.activeElement === document.body;
  view = 'list'; drawNav(); frame(); nav.scrollTop = 0;
  if (refocus && !document.activeElement?.offsetParent) nav.querySelector(`.setnav-i[data-g="${cur}"]`)?.focus({ preventScroll: true }); // back on the row I came from
}

let back = null, from = null;
function open(on, keyboard = false, where) {
  const was = shown();
  if (on && was && where) { if (target(where)) go(where, keyboard); else want = where; return; } // (not there yet: when it comes)
  if (on === was) { if (on && keyboard) x.focus(); return; }
  back.style.display = on ? '' : 'none';
  button.classList.toggle('on', on);
  button.setAttribute('aria-expanded', String(on));
  document.documentElement.classList.toggle('settings-open', on);
  // Opened from the keyboard: focus goes into the window, and back where it was (「设置」) when it closes. Opened with the
  // mouse, focus stays put (so Space on the map is still push-to-talk afterwards).
  if (on) {
    from = keyboard ? document.activeElement : null;
    find.value = '';
    want = where && !target(where) ? where : null;
    if (where && target(where)) go(where);
    else if (phone()) { if (!cur || !target(cur)) cur = groups()[0]?.id ?? null; toList(); }
    else { const w = want; go(cur && target(cur) ? cur : groups()[0]?.id); want = w; } // (the deep link's section may still come)
    if (keyboard) (nav.querySelector('.setnav-i.on') ?? x).focus();
  } else {
    want = null; hash('');
    if (panel.contains(document.activeElement)) { if (from?.isConnected && from !== document.body) from.focus(); else document.activeElement.blur(); }
  }
}

const button = el('button'); button.id = 'settingsBtn'; button.type = 'button'; button.setAttribute('aria-expanded', 'false'); button.setAttribute('aria-controls', 'settings');
back = el('div'); back.id = 'settingsBack'; back.style.display = 'none';
panel = el('div'); panel.id = 'settings'; panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'true');
const head = el('div', 'sethead');
backBtn = el('button', 'setback', '\u2039'); backBtn.type = 'button'; backBtn.style.display = 'none';
backBtn.onclick = () => toList();
title = el('b', 'settitle');
const x = el('button', 'setx'); x.append(icon('close')); x.type = 'button'; x.onclick = () => open(false);
head.append(backBtn, title, x);
nav = el('nav', 'setnav');
find = el('input', 'setfind'); find.type = 'search'; find.autocomplete = 'off'; find.enterKeyHint = 'search';
find.oninput = () => { if (phone()) view = 'list'; drawNav(); frame(); };
find.onkeydown = (e) => {
  if (e.key === 'Enter') { e.preventDefault(); nav.querySelector('.setnav-i.hit')?.click(); }
  else if (e.key === 'ArrowDown') { e.preventDefault(); nav.querySelector('.setnav-i')?.focus(); }
};
// ↑ ↓ move between the groups (or the results)
nav.addEventListener('keydown', (e) => {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' || !e.target.classList?.contains('setnav-i')) return;
  const all = [...nav.querySelectorAll('.setnav-i')], i = all.indexOf(e.target);
  e.preventDefault();
  if (e.key === 'ArrowUp' && i === 0) find.focus(); else all[Math.min(all.length - 1, Math.max(0, i + (e.key === 'ArrowDown' ? 1 : -1)))]?.focus();
});
nav.append(find);
pane = el('div', 'setpane'); pane.setAttribute('role', 'tabpanel');
const main = el('div', 'setmain'); main.append(nav, pane);
panel.append(head, main);
back.append(panel);
// the window's own words, again on a language change (and the open window drawn again: every section's text)
function words() {
  label(button, 'gear', t('settings.title')); panel.setAttribute('aria-label', t('settings.title'));
  x.title = t('settings.close'); x.setAttribute('aria-label', t('settings.closeLabel'));
  backBtn.title = t('settings.back'); backBtn.setAttribute('aria-label', t('settings.backLabel'));
  nav.setAttribute('aria-label', t('settings.nav')); find.placeholder = t('settings.find.placeholder'); find.setAttribute('aria-label', t('settings.find.placeholder'));
}
words();
onLang(() => {
  words();
  if (!shown()) return;
  // the redraw replaces what had focus (the 语言 dropdown, picked from the keyboard): focus stays in the window
  const had = panel.contains(document.activeElement) || document.activeElement === document.body;
  drawNav(); if (!phone() || view === 'pane') drawPane(); frame();
  if (had) (pane.querySelector('[data-id=language] .dd-btn') ?? x).focus({ preventScroll: true });
});
// a deep link: #settings, #settings/voice (at start, or pasted in later)
// (at start, after the boarding screen: 设置 over it would hide 上船)
const boarding = () => { const j = document.getElementById('join'); return !!j && getComputedStyle(j).display !== 'none'; };
const fromHash = () => {
  const m = /^#settings(?:\/([\w-]+))?$/.exec(location.hash); if (!m) return;
  if (!boarding()) { open(true, false, m[1] ?? (phone() ? undefined : 'account')); return; } // (a computer: 账号 first, as from 「设置」)
  const t = setInterval(() => { if (!/^#settings(\/|$)/.test(location.hash)) clearInterval(t); else if (!boarding()) { clearInterval(t); fromHash(); } }, 400);
};
const mount = () => { const tl = document.getElementById('topleft'); (tl || document.body).appendChild(button); document.body.appendChild(back); fromHash(); };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount); else mount();
addEventListener('hashchange', fromHash);
// turning a phone or resizing across 640 px: the list and the group side by side, or one at a time
matchMedia('(max-width:640px)').addEventListener?.('change', () => { if (!shown()) return; if (!phone()) go(cur ?? groups()[0]?.id); else if (view === 'pane') { drawNav(); frame(); } else toList(); });
button.onclick = (e) => { e.stopPropagation(); open(!shown(), e.detail === 0); }; // detail 0: Enter/Space
// a click on the dimmed backdrop (not in the window) closes it; a dropdown's list (public/dropdown.js) is on the body, but it's the window's
back.addEventListener('pointerdown', (e) => { if (e.target === back) { e.preventDefault(); open(false); } });
// a dialog opened from inside (邀请访客, public/guests.js #guestDlg, z 90) is above the window: its keys are its own
const above = () => !!document.querySelector('.dd-list, #guestDlg');
addEventListener('keydown', (e) => {
  if (!shown()) return;
  if (e.key === 'Escape' && !above() && e.target === find && find.value) { find.value = ''; drawNav(); e.stopImmediatePropagation(); e.preventDefault(); return; } // Esc in 搜索 clears it first
  if (e.key === 'Escape' && !above()) { open(false); e.stopImmediatePropagation(); e.preventDefault(); return; } // an open dropdown (public/dropdown.js) takes Esc first
  // Tab stays in the window (a modal)
  if (e.key === 'Tab' && !above()) {
    const f = [...panel.querySelectorAll('button,a[href],input,select,textarea,[tabindex]:not([tabindex="-1"])')].filter((n) => !n.disabled && n.offsetParent);
    if (f.length && (e.shiftKey ? document.activeElement === f[0] || !panel.contains(document.activeElement) : document.activeElement === f[f.length - 1])) { e.preventDefault(); (e.shiftKey ? f[f.length - 1] : f[0]).focus(); }
  }
}, true);

/** A line of text with links in it: parts are strings or [text, href]. */
function line(parts, cls) {
  const p = el('p', cls);
  for (const part of parts) {
    if (typeof part === 'string') p.append(document.createTextNode(part));
    else { const a = el('a', '', part[0]); a.href = part[1]; a.target = '_blank'; a.rel = 'noopener noreferrer'; p.append(a); }
  }
  return p;
}

// 语言: 自动 (the Raft account's, else the browser's) or one of the catalogs (public/i18n.js); each language named in itself
addSettingsSection({
  id: 'language', title: () => t('settings.lang.title'), icon: 'text', order: 1,
  render(box) {
    const options = [{ value: 'auto', text: t('settings.lang.auto'), hint: t('settings.lang.autoHint') }, ...LANGS.map((l) => ({ value: l, text: t(`lang.name.${l}`) }))];
    const dd = dropdown({ label: t('settings.lang.title'), className: 'dd-field', options, value: langPref(), onChange: async (v) => { if (!(await setLang(v))) dd.set(langPref()); } }); // no catalog (offline): stays as it was
    box.append(dd.el);
  },
});

addSettingsSection({
  id: 'about', title: () => t('settings.about.title'), order: 1000,
  render(box) {
    box.append(
      line([t('settings.about.what')]),
      el('h4', '', t('settings.about.words')),
      // the links sit where the sentence has {quickdraw} and {thuocl}, wherever each language puts them
      line(tParts('settings.about.pictionary', { quickdraw: ['Google Quick, Draw!', 'https://github.com/googlecreativelab/quickdraw-dataset'], thuocl: [t('settings.about.thuocl'), 'https://github.com/thunlp/THUOCL'] })),
      line([t('settings.about.spy')]),
    );
  },
});
